import cx from 'classnames'
import { motion } from 'motion/react'
import { FC, useMemo } from 'react'
import {
  motionGroup,
  separatorReveal,
  slideReveal,
  useSkipRevealOnAnchor,
} from 'src/hooks/useAppear'
import { MotionMask, MotionWrapper, SeparatorAbsolute } from 'src/theme/styled'

import { colors } from '../../theme'
import { ActionButtonProps } from '../ActionButton'
import { Icons } from '../Icon'
import RichText, { RichTextProps } from '../RichText'

import * as SC from './styled'
import { BlockTitleVariant } from './types'

export type BlockTitleProps = MinimalSeoBlock & {
  className?: string
  title: string
  richTextProps?: RichTextProps
  color?: string
  buttonColor?: string
  tag?: string
  actionButtonProps?: ActionButtonProps
  instant?: boolean
}

const BlockTitle: FC<BlockTitleProps> = ({
  className,
  htmlTag,
  htmlTitleTag,
  title,
  richTextProps,
  color = colors.yellowPale,
  tag,
  actionButtonProps,
  instant,
}) => {
  const variant = useMemo(() => {
    return tag
      ? BlockTitleVariant.tagged
      : actionButtonProps
        ? BlockTitleVariant.buttoned
        : BlockTitleVariant.default
  }, [tag, actionButtonProps])

  const { motions, variants } = useBlockTitleMotion()
  const { ref: anchorRef, skip: skipViaAnchor } =
    useSkipRevealOnAnchor<HTMLElement>()

  const MotionContainer = motion.create(SC.Container)

  const shouldSkipAnimation = instant || skipViaAnchor

  const containerMotionProps = shouldSkipAnimation
    ? { initial: 'show', animate: 'show', variants: motions.container.variants }
    : motions.container

  return (
    <MotionContainer
      ref={anchorRef}
      $variant={variant}
      as={htmlTag}
      className={cx('BlockTitle', className)}
      {...containerMotionProps}
    >
      <>
        <SeparatorAbsolute
          variants={variants.separator}
          role="separator"
          aria-hidden
        />
        {variant === BlockTitleVariant.tagged && tag && (
          <SC.Tag>
            <SC.TagContent variants={variants.tag}>
              <SC.TagIcon icon={Icons.bigTag} $color={color} /> {tag}
            </SC.TagContent>
          </SC.Tag>
        )}
        <SC.Title as={htmlTitleTag} $variant={variant}>
          <SC.TitleContent
            variants={variants.title(variant === BlockTitleVariant.tagged)}
          >
            {title}
          </SC.TitleContent>
        </SC.Title>
        {variant === BlockTitleVariant.buttoned && actionButtonProps ? (
          <MotionMask>
            <MotionWrapper variants={variants.actionOrText}>
              <SC._ActionButton {...actionButtonProps} />
            </MotionWrapper>
          </MotionMask>
        ) : (
          richTextProps && (
            <SC.RichTextMask>
              <MotionWrapper variants={variants.actionOrText}>
                <RichText {...richTextProps} />
              </MotionWrapper>
            </SC.RichTextMask>
          )
        )}
      </>
    </MotionContainer>
  )
}

const useBlockTitleMotion = () => {
  const motions = useMemo(
    () => ({
      container: motionGroup({}).motionProps,
    }),
    []
  )

  const variants = useMemo(
    () => ({
      separator: separatorReveal({}),
      tag: slideReveal({ y: '100%', delay: 0.2 }),
      title: (tagged: boolean) =>
        slideReveal({ y: '100%', delay: tagged ? 0.3 : 0 }),
      actionOrText: slideReveal({ y: '100%', delay: 0.4 }),
    }),
    []
  )

  return { motions, variants }
}

export default BlockTitle
